import React, { useState, useEffect } from 'react';
import {
  Search, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw,
  Building2, Smartphone, DollarSign, ArrowUpRight, Copy, Check,
  User, ExternalLink, ShieldCheck, Filter, FileText, ChevronRight,
  Calendar, Settings, QrCode, Star, ChevronDown, ChevronUp, Save, Eye, EyeOff,
  TrendingUp, Plus, Trash2
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { withdrawalsAPI } from '../utils/api';
import { FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';

const WithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({
    total_requests: 0,
    pending_count: 0,
    pending_amount: 0,
    paid_count: 0,
    paid_amount: 0,
    rejected_count: 0,
    rejected_amount: 0,
    total_diamonds_withdrawn: 0,
    total_coins_withdrawn: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // 'all', 'pending', 'paid', 'rejected'
  const [payoutTypeFilter, setPayoutTypeFilter] = useState('all'); // 'all', 'bank', 'upi'
  const [countryFilter, setCountryFilter] = useState('ALL');

  // Modals state
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [modalType, setModalType] = useState(null); // 'details', 'approve', 'reject'
  const [transactionRef, setTransactionRef] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  // Withdrawal Config & Window Controls
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isSellersOpen, setIsSellersOpen] = useState(false);
  const [withdrawalConfig, setWithdrawalConfig] = useState({
    withdrawal_enabled: true,
    withdrawal_hide_start_date: '',
    withdrawal_hide_end_date: '',
    withdrawal_allowed_start_day: 1,
    withdrawal_allowed_end_day: 5,
    withdrawal_status_message: 'Withdrawals are currently closed.',
    admin_upi_id: '',
    admin_qr_code_url: ''
  });
  const [topSellers, setTopSellers] = useState([]);
  const [configSaving, setConfigSaving] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // Withdrawal Target Slabs (1 Diamond = 1 Coin, USD & INR support)
  const [isSlabsModalOpen, setIsSlabsModalOpen] = useState(false);
  const [targetSlabs, setTargetSlabs] = useState([]);
  const [usdRate, setUsdRate] = useState('90.00');
  const [loadingSlabs, setLoadingSlabs] = useState(false);
  const [savingSlabs, setSavingSlabs] = useState(false);
  const [slabFeedback, setSlabFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchWithdrawals();
    fetchStats();
    fetchWithdrawalConfig();
    fetchTopSellers();
    fetchTargetSlabs();
  }, [statusFilter]);

  const fetchTargetSlabs = async () => {
    setLoadingSlabs(true);
    try {
      const res = await withdrawalsAPI.getWithdrawalTargetSlabs();
      if (res.data?.success) {
        const slabs = (res.data.data?.slabs || []).map((s, idx) => ({
          ...s,
          target_title: s.target_title || `Tier ${s.sort_order || idx + 1}`,
          diamonds: s.diamonds || s.target_diamonds || 0,
          coins: s.coins || s.target_coins || s.target_diamonds || 0,
          amount_usd: s.amount_usd !== undefined ? String(s.amount_usd) : '0.00',
          amount_inr: s.amount_inr !== undefined ? String(s.amount_inr) : '0.00',
        }));
        setTargetSlabs(slabs);
        setUsdRate(String(res.data.data?.usd_to_inr_rate || '90.00'));
      }
    } catch (err) {
      console.error("Error fetching withdrawal slabs:", err);
    } finally {
      setLoadingSlabs(false);
    }
  };

  const handleSlabChange = (idx, field, value) => {
    const updated = [...targetSlabs];
    const current = { ...updated[idx], [field]: value };
    const rate = parseFloat(usdRate) || 90;

    // 1 Diamond = 1 Coin ratio
    if (field === 'diamonds') {
      current.coins = value;
      current.target_diamonds = value;
      current.target_coins = value;
    } else if (field === 'coins') {
      current.diamonds = value;
      current.target_diamonds = value;
      current.target_coins = value;
    } else if (field === 'amount_usd') {
      const usdVal = parseFloat(value) || 0;
      current.amount_inr = (usdVal * rate).toFixed(2);
    } else if (field === 'amount_inr') {
      const inrVal = parseFloat(value) || 0;
      current.amount_usd = (inrVal / rate).toFixed(2);
    }

    updated[idx] = current;
    setTargetSlabs(updated);
  };

  const handleRateChange = (newRate) => {
    setUsdRate(newRate);
    const rateNum = parseFloat(newRate);
    if (rateNum && rateNum > 0) {
      setTargetSlabs(prev => prev.map(s => {
        const usd = parseFloat(s.amount_usd) || 0;
        return {
          ...s,
          amount_inr: (usd * rateNum).toFixed(2)
        };
      }));
    }
  };

  const handleAddSlab = () => {
    const rate = parseFloat(usdRate) || 90;
    const nextSort = targetSlabs.length + 1;
    const newSlab = {
      id: `temp_${Date.now()}`,
      target_title: `Tier ${nextSort}`,
      diamonds: 100000,
      coins: 100000,
      target_diamonds: 100000,
      target_coins: 100000,
      amount_usd: '10.00',
      amount_inr: (10 * rate).toFixed(2),
      is_active: true,
      sort_order: nextSort
    };
    setTargetSlabs([...targetSlabs, newSlab]);
  };

  const handleDeleteSlab = (idx) => {
    const updated = targetSlabs.filter((_, i) => i !== idx);
    setTargetSlabs(updated);
  };

  const handleSaveSlabs = async () => {
    setSavingSlabs(true);
    setSlabFeedback({ type: '', message: '' });
    try {
      const res = await withdrawalsAPI.updateWithdrawalTargetSlabs({
        slabs: targetSlabs,
        usd_to_inr_rate: usdRate
      });
      if (res.data?.success) {
        setSlabFeedback({ type: 'success', message: res.data.message || "Withdrawal target slabs saved successfully!" });
        fetchTargetSlabs();
      }
    } catch (err) {
      setSlabFeedback({ type: 'error', message: err.response?.data?.message || "Failed to save withdrawal slabs" });
    } finally {
      setSavingSlabs(false);
    }
  };

  const fetchWithdrawalConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await withdrawalsAPI.getConfig();
      if (res.data?.success && res.data?.data) {
        setWithdrawalConfig(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching withdrawal config:", err);
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchTopSellers = async () => {
    try {
      const res = await withdrawalsAPI.getTopSellers();
      if (res.data?.success && res.data?.data) {
        setTopSellers(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching top sellers:", err);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setConfigSaving(true);
    try {
      const res = await withdrawalsAPI.updateConfig(withdrawalConfig);
      alert("Withdrawal settings updated successfully!");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update withdrawal settings");
    } finally {
      setConfigSaving(false);
    }
  };

  const getHeaders = () => ({
    headers: {
      Authorization: sessionStorage.getItem('adminToken')
    }
  });

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${constant.API_BASE_URL}/admin/withdrawals?status=${statusFilter}`,
        getHeaders()
      );
      if (res.data.success) {
        setWithdrawals(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(
        `${constant.API_BASE_URL}/admin/withdrawals/stats`,
        getHeaders()
      );
      if (res.data.success) {
        setStats(res.data.data || {});
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleCopy = (text, fieldKey) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const openActionModal = (item, type) => {
    setSelectedWithdrawal(item);
    setModalType(type);
    setTransactionRef(item.transaction_ref || '');
    setAdminNotes(item.admin_notes || '');
  };

  const closeModal = () => {
    setSelectedWithdrawal(null);
    setModalType(null);
    setTransactionRef('');
    setAdminNotes('');
  };

  const handleApprove = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    try {
      const res = await axios.put(
        `${constant.API_BASE_URL}/admin/withdrawals/${selectedWithdrawal.id}/status`,
        {
          status: 'paid',
          transaction_ref: transactionRef,
          admin_notes: adminNotes || 'Payout processed successfully'
        },
        getHeaders()
      );

      if (res.data.success) {
        closeModal();
        fetchWithdrawals();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve withdrawal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;
    if (!adminNotes.trim()) {
      alert("Please provide a reason for rejecting the withdrawal request.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.put(
        `${constant.API_BASE_URL}/admin/withdrawals/${selectedWithdrawal.id}/status`,
        {
          status: 'rejected',
          admin_notes: adminNotes
        },
        getHeaders()
      );

      if (res.data.success) {
        closeModal();
        fetchWithdrawals();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reject withdrawal");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredWithdrawals = withdrawals.filter(item => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (item.user_name && item.user_name.toLowerCase().includes(term)) ||
      (item.email && item.email.toLowerCase().includes(term)) ||
      (item.xoxo_id && item.xoxo_id.toLowerCase().includes(term)) ||
      (item.country && item.country.toLowerCase().includes(term)) ||
      (item.account_holder_name && item.account_holder_name.toLowerCase().includes(term)) ||
      (item.account_number && item.account_number.toLowerCase().includes(term)) ||
      (item.ifsc_code && item.ifsc_code.toLowerCase().includes(term)) ||
      (item.upi_id && item.upi_id.toLowerCase().includes(term)) ||
      (item.transaction_ref && item.transaction_ref.toLowerCase().includes(term));

    const matchesPayoutType = payoutTypeFilter === 'all' || item.payout_type === payoutTypeFilter;
    const matchesCountry = countryFilter === 'ALL' || (item.country && item.country.toLowerCase() === countryFilter.toLowerCase());
    return matchesSearch && matchesPayoutType && matchesCountry;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
            <span>Withdrawal Requests</span>
            {Number(stats.pending_count) > 0 && (
              <span className="px-3 py-1 bg-amber-500/10 text-amber-600 text-sm font-bold rounded-full border border-amber-500/20">
                {stats.pending_count} Pending
              </span>
            )}
          </h2>
          <p className="text-slate-500 mt-1">
            Review user payout requests, verify bank/UPI accounts, transfer funds, and mark payouts as completed.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <button
            onClick={() => setIsSellersOpen(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold rounded-xl hover:opacity-95 transition active:scale-95 shadow-md shadow-amber-500/20 flex items-center gap-2 text-sm"
          >
            <Star size={16} />
            <span>Top 10 Coin Sellers</span>
          </button>
          <button
            onClick={() => setIsConfigOpen(!isConfigOpen)}
            className={`px-4 py-2.5 rounded-xl font-bold transition active:scale-95 shadow-sm flex items-center gap-2 text-sm ${
              isConfigOpen 
                ? 'bg-slate-900 text-white' 
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Settings size={16} />
            <span>Withdrawal Dates & QR</span>
            {withdrawalConfig.withdrawal_hide_start_date && (
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            )}
          </button>
          <button
            onClick={() => {
              setIsSlabsModalOpen(true);
              fetchTargetSlabs();
            }}
            className={`px-4 py-2.5 rounded-xl font-bold transition active:scale-95 shadow-sm flex items-center gap-2 text-sm ${
              isSlabsModalOpen 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            <TrendingUp size={16} className={isSlabsModalOpen ? "text-white" : "text-emerald-600"} />
            <span>Target Slabs (USD/INR)</span>
          </button>
          <button
            onClick={() => {
              fetchWithdrawals();
              fetchStats();
              fetchWithdrawalConfig();
            }}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm flex items-center gap-2 text-sm"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Date Window & Rules Configuration Panel */}
      {isConfigOpen && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xl space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div>
              <h3 className="text-xl font-black text-slate-800 flex items-center gap-2.5">
                <Calendar className="text-pink-600" size={22} />
                Withdrawal Schedule & Admin QR Settings
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Configure specific dates or monthly windows where the mobile app hides the withdrawal button.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                !withdrawalConfig.withdrawal_enabled
                  ? 'bg-rose-100 text-rose-700'
                  : (withdrawalConfig.withdrawal_hide_start_date && withdrawalConfig.withdrawal_hide_end_date)
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-emerald-100 text-emerald-700'
              }`}>
                {!withdrawalConfig.withdrawal_enabled 
                  ? '🔴 Globally Disabled' 
                  : (withdrawalConfig.withdrawal_hide_start_date && withdrawalConfig.withdrawal_hide_end_date)
                    ? '🟡 Date Window Active'
                    : '🟢 Withdrawals Allowed'}
              </span>
            </div>
          </div>

          <form onSubmit={handleSaveConfig} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Global Enable Toggle */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                    Platform Withdrawal Status
                  </label>
                  <p className="text-xs text-slate-400 mb-3">Master switch to enable or disable withdrawals completely.</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_enabled: !withdrawalConfig.withdrawal_enabled })}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      withdrawalConfig.withdrawal_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        withdrawalConfig.withdrawal_enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-bold text-slate-700">
                    {withdrawalConfig.withdrawal_enabled ? 'Withdrawals Enabled' : 'Withdrawals Blocked'}
                  </span>
                </div>
              </div>

              {/* Hide From Date */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                  Hide Button From Date
                </label>
                <p className="text-xs text-slate-400 mb-2">Start date to hide button in mobile app</p>
                <input
                  type="date"
                  value={withdrawalConfig.withdrawal_hide_start_date ? withdrawalConfig.withdrawal_hide_start_date.substring(0, 10) : ''}
                  onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_hide_start_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-pink-500/20"
                />
              </div>

              {/* Hide To Date */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                  Hide Button To Date
                </label>
                <p className="text-xs text-slate-400 mb-2">End date to keep button hidden in app</p>
                <input
                  type="date"
                  value={withdrawalConfig.withdrawal_hide_end_date ? withdrawalConfig.withdrawal_hide_end_date.substring(0, 10) : ''}
                  onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_hide_end_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-pink-500/20"
                />
              </div>

              {/* Clear Hide Window Button */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                    Clear Date Restrictions
                  </label>
                  <p className="text-xs text-slate-400 mb-3">Instantly restore withdrawal button in app.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setWithdrawalConfig({
                    ...withdrawalConfig,
                    withdrawal_hide_start_date: '',
                    withdrawal_hide_end_date: ''
                  })}
                  className="w-full py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-xl transition"
                >
                  Clear Hide Dates
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Monthly Recurring Day Window */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                  Monthly Allowed Day Range (1 - 31)
                </label>
                <p className="text-xs text-slate-400 mb-3">e.g. From day 1 to day 5 of every month</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={withdrawalConfig.withdrawal_allowed_start_day || 1}
                    onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_allowed_start_day: parseInt(e.target.value) || 1 })}
                    className="w-20 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-center text-slate-700"
                  />
                  <span className="text-slate-400 font-bold text-xs">to</span>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={withdrawalConfig.withdrawal_allowed_end_day || 31}
                    onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_allowed_end_day: parseInt(e.target.value) || 31 })}
                    className="w-20 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-center text-slate-700"
                  />
                  <span className="text-slate-500 text-xs font-semibold">of each month</span>
                </div>
              </div>

              {/* Status Notice Message */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 md:col-span-2">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1">
                  Locked Notice Message (Shown in App)
                </label>
                <p className="text-xs text-slate-400 mb-2">Message displayed when a user attempts withdrawal during closed hours</p>
                <input
                  type="text"
                  value={withdrawalConfig.withdrawal_status_message || ''}
                  onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, withdrawal_status_message: e.target.value })}
                  placeholder="e.g. Withdrawals are currently closed. Payouts open from 1st to 5th of each month."
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-pink-500/20"
                />
              </div>
            </div>

            {/* Admin QR Code & UPI Configuration */}
            <div className="p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100 grid grid-cols-1 md:grid-cols-3 gap-5 items-center">
              <div className="md:col-span-2 space-y-3">
                <h4 className="text-sm font-black text-slate-800 flex items-center gap-2">
                  <QrCode size={18} className="text-purple-600" />
                  Admin UPI & Payment QR Code for Deposits/Withdrawals
                </h4>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Admin UPI ID</label>
                  <input
                    type="text"
                    value={withdrawalConfig.admin_upi_id || ''}
                    onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, admin_upi_id: e.target.value })}
                    placeholder="e.g. xoxopay@okaxis or admin@upi"
                    className="w-full px-3 py-2 bg-white border border-purple-200 rounded-xl text-sm font-semibold text-slate-700"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Admin QR Code Image URL</label>
                  <input
                    type="text"
                    value={withdrawalConfig.admin_qr_code_url || ''}
                    onChange={(e) => setWithdrawalConfig({ ...withdrawalConfig, admin_qr_code_url: e.target.value })}
                    placeholder="https://.../admin_upi_qr.png"
                    className="w-full px-3 py-2 bg-white border border-purple-200 rounded-xl text-sm font-semibold text-slate-700"
                  />
                </div>
              </div>

              {/* QR Preview */}
              <div className="flex flex-col items-center justify-center p-3 bg-white rounded-2xl border border-purple-200 shadow-sm">
                <span className="text-[11px] font-bold text-slate-400 uppercase mb-2">QR Code Preview</span>
                {withdrawalConfig.admin_qr_code_url ? (
                  <img
                    src={withdrawalConfig.admin_qr_code_url}
                    alt="Admin UPI QR"
                    className="w-28 h-28 object-contain rounded-lg border border-slate-100"
                  />
                ) : (
                  <div className="w-28 h-28 rounded-lg bg-purple-50 border border-purple-200 border-dashed flex flex-col items-center justify-center text-purple-400 text-xs text-center p-2">
                    <QrCode size={32} className="mb-1 opacity-50" />
                    No QR Image
                  </div>
                )}
                {withdrawalConfig.admin_upi_id && (
                  <span className="text-xs font-mono font-bold text-purple-700 mt-2">{withdrawalConfig.admin_upi_id}</span>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsConfigOpen(false)}
                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={configSaving}
                className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-95 text-white font-bold rounded-xl text-sm shadow-md shadow-pink-500/20 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Save size={16} />
                {configSaving ? 'Saving Changes...' : 'Save Withdrawal Settings'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-5 rounded-[22px] border border-amber-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Pending Payouts</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.pending_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.pending_count || 0} requests awaiting transfer</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <Clock size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-emerald-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Completed / Paid</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.paid_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.paid_count || 0} successful payouts</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <CheckCircle size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-rose-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-rose-600 uppercase tracking-wider">Rejected Requests</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.rejected_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.rejected_count || 0} refunded to users</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
              <XCircle size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-rose-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-cyan-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider">Total Volume</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">
                {Number(stats.total_diamonds_withdrawn || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Total Diamonds Withdrawn</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center font-bold">
              💎
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-500"></div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Filter Navigation Tabs */}
        <div className="p-4 sm:p-6 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-wrap border-b border-slate-200 lg:border-none gap-2 sm:gap-4">
            <button
              onClick={() => setStatusFilter('pending')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'pending'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Clock size={16} />
              <span>Pending Requests ({stats.pending_count || 0})</span>
              {Number(stats.pending_count) > 0 && (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
              )}
            </button>
            <button
              onClick={() => setStatusFilter('paid')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'paid'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <CheckCircle size={16} />
              <span>Paid / Completed ({stats.paid_count || 0})</span>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'rejected'
                  ? 'border-rose-600 text-rose-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <XCircle size={16} />
              <span>Rejected ({stats.rejected_count || 0})</span>
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'all'
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <FileText size={16} />
              <span>All ({stats.total_requests || 0})</span>
            </button>
          </div>

          {/* Search & Payout Type & Country Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
              <input
                type="text"
                placeholder="Search user, UPI, A/C, UTR..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition"
              />
            </div>
            <div className="w-full sm:w-48">
              <FilterCountrySelect
                value={countryFilter}
                onChange={setCountryFilter}
                placeholder="All Countries"
              />
            </div>
            <select
              value={payoutTypeFilter}
              onChange={(e) => setPayoutTypeFilter(e.target.value)}
              className="w-full sm:w-auto px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-pink-500/20"
            >
              <option value="all">All Payout Methods</option>
              <option value="bank">Bank Account</option>
              <option value="upi">UPI ID</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                <th className="py-4 px-6">User / Requester</th>
                <th className="py-4 px-6">Location</th>
                <th className="py-4 px-6">Payout Amount</th>
                <th className="py-4 px-6">Payment Destination</th>
                <th className="py-4 px-6">Requested At</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-400">
                    <RefreshCw className="animate-spin inline mr-2" size={18} />
                    Loading withdrawal requests...
                  </td>
                </tr>
              ) : filteredWithdrawals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-16 text-center text-slate-400">
                    <div className="w-16 h-16 rounded-full bg-slate-50 text-slate-300 flex items-center justify-center mx-auto mb-3">
                      <FileText size={28} />
                    </div>
                    <p className="font-semibold text-slate-600">No withdrawal requests found</p>
                    <p className="text-xs text-slate-400 mt-1">Try changing filters or search terms</p>
                  </td>
                </tr>
              ) : (
                filteredWithdrawals.map((item) => {
                  const isBank = item.payout_type === 'bank';
                  return (
                    <tr key={item.id} className="hover:bg-slate-50/70 transition">
                      {/* User Column */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm overflow-hidden shrink-0 shadow-sm">
                            {item.profile_pic ? (
                              <img src={item.profile_pic} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (item.user_name || 'U').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{item.user_name || 'Anonymous User'}</div>
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                              <span>ID: <b className="text-slate-600">{item.xoxo_id || item.user_id?.substring(0, 8)}</b></span>
                              {item.phone_number && <span>• {item.phone_number}</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Location Column */}
                      <td className="py-4 px-6">
                        <CountryBadge country={item.country} />
                      </td>

                      {/* Amount Column */}
                      <td className="py-4 px-6">
                        <div className="font-extrabold text-slate-900 text-base">
                          ₹{Number(item.amount_inr || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                          {Number(item.diamonds) > 0 && (
                            <span className="text-cyan-600 font-semibold">💎 {Number(item.diamonds).toLocaleString()}</span>
                          )}
                          {Number(item.coins) > 0 && (
                            <span className="text-amber-600 font-semibold">🪙 {Number(item.coins).toLocaleString()}</span>
                          )}
                        </div>
                      </td>

                      {/* Payment Destination Column */}
                      <td className="py-4 px-6">
                        {isBank ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-md flex items-center gap-1">
                                <Building2 size={12} /> Bank
                              </span>
                              <span className="font-semibold text-slate-800 text-xs">
                                {item.account_holder_name}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600 flex items-center gap-2 font-mono">
                              <span>A/C: {item.account_number}</span>
                              <button
                                onClick={() => handleCopy(item.account_number, `acc_${item.id}`)}
                                className="text-slate-400 hover:text-slate-700 transition"
                                title="Copy Account Number"
                              >
                                {copiedField === `acc_${item.id}` ? (
                                  <Check size={13} className="text-emerald-500" />
                                ) : (
                                  <Copy size={13} />
                                )}
                              </button>
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono">
                              IFSC: {item.ifsc_code}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[11px] font-bold rounded-md flex items-center gap-1">
                                <Smartphone size={12} /> UPI
                              </span>
                              {item.account_holder_name && (
                                <span className="font-semibold text-slate-800 text-xs">
                                  {item.account_holder_name}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-700 font-mono flex items-center gap-2">
                              <span className="font-bold text-purple-900">{item.upi_id}</span>
                              <button
                                onClick={() => handleCopy(item.upi_id, `upi_${item.id}`)}
                                className="text-slate-400 hover:text-slate-700 transition"
                                title="Copy UPI ID"
                              >
                                {copiedField === `upi_${item.id}` ? (
                                  <Check size={13} className="text-emerald-500" />
                                ) : (
                                  <Copy size={13} />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Date Column */}
                      <td className="py-4 px-6">
                        <div className="text-xs text-slate-600 font-medium">
                          {new Date(item.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {new Date(item.created_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>

                      {/* Status Column */}
                      <td className="py-4 px-6">
                        {item.status === 'pending' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold rounded-full">
                            <Clock size={12} /> Pending Payout (Admin)
                          </span>
                        )}
                        {item.status === 'pending_seller' && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 border border-purple-200 text-xs font-bold rounded-full">
                              <Clock size={12} /> Assigned to Seller
                            </span>
                            {item.seller_name && (
                              <div className="text-[11px] font-semibold text-purple-600">
                                {item.seller_name} {item.seller_xoxo_id ? `(${item.seller_xoxo_id})` : ''}
                              </div>
                            )}
                          </div>
                        )}
                        {item.status === 'accepted_by_seller' && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold rounded-full">
                              <Clock size={12} /> Seller Paying...
                            </span>
                            {item.seller_name && (
                              <div className="text-[11px] font-semibold text-blue-600">
                                {item.seller_name}
                              </div>
                            )}
                          </div>
                        )}
                        {(item.status === 'paid' || item.status === 'approved') && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-full">
                              <CheckCircle size={12} /> Paid
                            </span>
                            {item.seller_name && (
                              <div className="text-[10px] text-emerald-600 font-medium">
                                via Seller: {item.seller_name}
                              </div>
                            )}
                            {item.transaction_ref && (
                              <div className="text-[10px] text-slate-400 font-mono truncate max-w-[130px]" title={item.transaction_ref}>
                                Ref: {item.transaction_ref}
                              </div>
                            )}
                          </div>
                        )}
                        {item.status === 'rejected' && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200 text-xs font-bold rounded-full">
                              <XCircle size={12} /> Rejected (Refunded)
                            </span>
                            {item.admin_notes && (
                              <div className="text-[10px] text-rose-400 truncate max-w-[140px]" title={item.admin_notes}>
                                {item.admin_notes}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Actions Column */}
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(item.status === 'pending_seller' || item.status === 'accepted_by_seller') && (
                            <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 px-3 py-1.5 rounded-xl">
                              Pending Seller Payout
                            </span>
                          )}
                          {item.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => openActionModal(item, 'approve')}
                                className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition active:scale-95 flex items-center gap-1"
                              >
                                <CheckCircle size={14} />
                                <span>Mark Paid</span>
                              </button>
                              <button
                                onClick={() => openActionModal(item, 'reject')}
                                className="px-3.5 py-1.5 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold rounded-xl transition active:scale-95 flex items-center gap-1"
                              >
                                <XCircle size={14} />
                                <span>Reject</span>
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => openActionModal(item, 'details')}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition flex items-center gap-1"
                            >
                              <span>View Details</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* APPROVE / MARK PAID MODAL */}
      {/* ========================================================================= */}
      {modalType === 'approve' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5 text-emerald-600">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center font-bold">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Confirm Payout Completion</h3>
                  <p className="text-xs text-slate-400">Mark this withdrawal request as Paid</p>
                </div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            {/* Payout Summary Card */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2.5 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Recipient:</span>
                <span className="font-bold text-slate-800">{selectedWithdrawal.user_name} ({selectedWithdrawal.email})</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Amount to Transfer:</span>
                <span className="font-extrabold text-emerald-600 text-lg">₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Payment Mode:</span>
                <span className="font-semibold text-slate-800 uppercase">{selectedWithdrawal.payout_type}</span>
              </div>
              {selectedWithdrawal.payout_type === 'bank' ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">A/C Holder:</span>
                    <span className="font-mono text-slate-800 font-semibold">{selectedWithdrawal.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Account No:</span>
                    <span className="font-mono text-slate-800 font-bold">{selectedWithdrawal.account_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">IFSC Code:</span>
                    <span className="font-mono text-slate-800">{selectedWithdrawal.ifsc_code}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">UPI ID:</span>
                  <span className="font-mono text-purple-700 font-bold">{selectedWithdrawal.upi_id}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Bank Reference / UTR Number (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. UTR123456789 / IMPS Ref"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Admin Note / Remarks
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sent via NetBanking on 14 Aug"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition active:scale-95 disabled:opacity-50"
              >
                {actionLoading ? "Processing..." : "Confirm Paid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* REJECT MODAL */}
      {/* ========================================================================= */}
      {modalType === 'reject' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5 text-rose-600">
                <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center font-bold">
                  <XCircle size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Reject Withdrawal Request</h3>
                  <p className="text-xs text-slate-400">Refunds diamonds/coins automatically back to user</p>
                </div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-4 bg-rose-50/50 rounded-2xl border border-rose-100 space-y-2 text-sm text-slate-700">
              <p className="font-semibold text-rose-800">
                Are you sure you want to reject this payout request of ₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}?
              </p>
              <p className="text-xs text-rose-600">
                ⚠️ The user's deducted balance (💎 {Number(selectedWithdrawal.diamonds).toLocaleString()} Diamonds / 🪙 {Number(selectedWithdrawal.coins).toLocaleString()} Coins) will be <b>automatically refunded</b> to their wallet.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                Rejection Reason (Required) <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Invalid bank account details / IFSC mismatch. Please update and re-apply."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition active:scale-95 disabled:opacity-50"
              >
                {actionLoading ? "Processing Refund..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DETAILS MODAL */}
      {/* ========================================================================= */}
      {modalType === 'details' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-extrabold text-slate-800 text-lg">Withdrawal Details</h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Request ID</span>
                <span className="font-mono text-slate-800 font-semibold">{selectedWithdrawal.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">User</span>
                <span className="font-bold text-slate-800">{selectedWithdrawal.user_name} ({selectedWithdrawal.email})</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Payout Amount</span>
                <span className="font-extrabold text-slate-900 text-base">₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Diamonds / Coins</span>
                <span className="font-semibold text-slate-700">
                  💎 {Number(selectedWithdrawal.diamonds || 0).toLocaleString()} / 🪙 {Number(selectedWithdrawal.coins || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Payout Mode</span>
                <span className="font-bold uppercase text-slate-800">{selectedWithdrawal.payout_type}</span>
              </div>
              {selectedWithdrawal.payout_type === 'bank' ? (
                <>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">Account Holder</span>
                    <span className="font-semibold text-slate-800">{selectedWithdrawal.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">Account Number</span>
                    <span className="font-mono font-bold text-slate-800">{selectedWithdrawal.account_number}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">IFSC Code</span>
                    <span className="font-mono text-slate-800">{selectedWithdrawal.ifsc_code}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">UPI ID</span>
                  <span className="font-mono font-bold text-purple-800">{selectedWithdrawal.upi_id}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Status</span>
                <span className="font-bold uppercase text-slate-800">{selectedWithdrawal.status}</span>
              </div>
              {selectedWithdrawal.transaction_ref && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Transaction Reference</span>
                  <span className="font-mono font-semibold text-emerald-700">{selectedWithdrawal.transaction_ref}</span>
                </div>
              )}
              {selectedWithdrawal.admin_notes && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Admin Notes</span>
                  <span className="text-slate-700 italic">{selectedWithdrawal.admin_notes}</span>
                </div>
              )}
              {selectedWithdrawal.processed_by_name && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Processed By</span>
                  <span className="font-semibold text-slate-800">{selectedWithdrawal.processed_by_name}</span>
                </div>
              )}
            </div>

            <button
              onClick={closeModal}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Top 10 Approved Coin Sellers Modal */}
      {isSellersOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-yellow-50 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <Star size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Top 10 Approved Coin Sellers</h3>
                  <p className="text-xs text-slate-500">Official Company Seller + Top 10 approved sellers by coin balance shown in mobile app</p>
                </div>
              </div>
              <button
                onClick={() => setIsSellersOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
              >
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-xs text-blue-800 font-medium">
                💡 <span className="font-bold">App Visibility Rule:</span> In the user's mobile coin purchase screen, only the Company Seller and the Top 10 admin-approved coin sellers with the highest coin balances are presented in the dropdown.
              </div>

              {topSellers.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No approved sellers found.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {topSellers.map((seller, idx) => (
                    <div key={seller.id || idx} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                          seller.is_company 
                            ? 'bg-purple-600 text-white' 
                            : idx === 1 ? 'bg-amber-400 text-white' : idx === 2 ? 'bg-slate-300 text-slate-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {seller.is_company ? '★' : `#${idx}`}
                        </span>
                        <div>
                          <div className="font-bold text-slate-800 flex items-center gap-2">
                            {seller.name}
                            {seller.is_company && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-[10px] font-black uppercase">
                                Official Company
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">
                            XOXO ID: <span className="font-mono font-bold text-pink-600">{seller.xoxo_id || 'N/A'}</span>
                            {seller.phone && ` • ${seller.phone}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-slate-900 text-sm">{parseInt(seller.coin_balance || 0).toLocaleString()} Coins</p>
                        <span className="text-[10px] font-bold text-emerald-600 uppercase bg-emerald-50 px-2 py-0.5 rounded-full">
                          {seller.level || 'Approved'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
              <button
                onClick={() => setIsSellersOpen(false)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold rounded-xl transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Target Slabs Modal (1 Diamond = 1 Coin, USD & INR support) */}
      {isSlabsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-4xl p-6 md:p-8 animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-200/60 flex items-center justify-center">
                  <TrendingUp size={22} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Withdrawal Target Slabs</h3>
                  <p className="text-xs text-slate-500">Configure diamond/coin target slabs with automatic 1 Diamond = 1 Coin ratio and dual USD & INR payouts</p>
                </div>
              </div>
              <button 
                onClick={() => setIsSlabsModalOpen(false)} 
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Exchange Rate Setting Bar */}
            <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <DollarSign size={20} className="text-emerald-700 shrink-0" />
                <div>
                  <span className="text-xs font-black uppercase text-emerald-900 tracking-wider">USD to INR Conversion Rate</span>
                  <p className="text-[11px] text-emerald-700">Used to convert slab amounts between USD ($) and Indian Rupees (₹)</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-600">1 USD ($) = ₹</span>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  value={usdRate}
                  onChange={(e) => handleRateChange(e.target.value)}
                  className="w-24 px-3 py-1.5 bg-white border border-emerald-300 rounded-xl font-black text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>
            </div>

            {slabFeedback.message && (
              <div className={`mt-3 p-3 rounded-xl text-xs font-semibold flex items-center gap-2 ${
                slabFeedback.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                {slabFeedback.type === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
                <span>{slabFeedback.message}</span>
              </div>
            )}

            {/* Slabs List */}
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {loadingSlabs ? (
                <div className="p-12 text-center text-slate-400">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm">Loading slabs...</p>
                </div>
              ) : targetSlabs.length === 0 ? (
                <div className="p-10 text-center text-slate-400">
                  <p className="text-sm font-bold">No withdrawal slabs defined yet.</p>
                  <button
                    onClick={handleAddSlab}
                    className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition"
                  >
                    + Add First Slab
                  </button>
                </div>
              ) : (
                targetSlabs.map((slab, idx) => (
                  <div key={slab.id || idx} className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                    <div className="sm:col-span-3">
                      <label className="block text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Tier Title</label>
                      <input
                        type="text"
                        value={slab.target_title || ''}
                        onChange={(e) => handleSlabChange(idx, 'target_title', e.target.value)}
                        placeholder="e.g. Tier 1 - 100K"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>

                    <div className="sm:col-span-3">
                      <label className="block text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">
                        Diamonds / Coins (1:1)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="1000"
                          min="1"
                          value={slab.diamonds ?? slab.coins ?? slab.target_diamonds ?? ''}
                          onChange={(e) => handleSlabChange(idx, 'diamonds', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black text-amber-600 outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">
                          💎=🪙
                        </span>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">USD Amount</label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">$</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={slab.amount_usd || ''}
                          onChange={(e) => handleSlabChange(idx, 'amount_usd', e.target.value)}
                          className="w-full pl-6 pr-2 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">INR Amount</label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">₹</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={slab.amount_inr || ''}
                          onChange={(e) => handleSlabChange(idx, 'amount_inr', e.target.value)}
                          className="w-full pl-6 pr-2 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-4 sm:pt-0">
                      <button
                        type="button"
                        onClick={() => handleSlabChange(idx, 'is_active', !slab.is_active)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${
                          slab.is_active !== false 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                        }`}
                      >
                        {slab.is_active !== false ? 'Active' : 'Off'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSlab(idx)}
                        className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete this tier"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}

              <button
                type="button"
                onClick={handleAddSlab}
                className="w-full py-2.5 border-2 border-dashed border-slate-200 hover:border-emerald-300 rounded-2xl text-xs font-bold text-slate-500 hover:text-emerald-700 transition flex items-center justify-center gap-1.5"
              >
                <Plus size={15} />
                <span>Add Another Target Tier</span>
              </button>
            </div>

            <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                1 Diamond = 1 Coin ratio enforced for all withdrawal tiers
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsSlabsModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSaveSlabs}
                  disabled={savingSlabs}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-xl text-xs shadow-md shadow-emerald-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  {savingSlabs ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={14} />
                      <span>Save Slabs & Rates</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalManagement;
